import { useState, useEffect, useReducer } from 'react';
import type { Team, TeamMember } from '@/types/api';
import { useAuthStore } from '@/stores/authStore';
import { useTeamStore } from '@/stores/teamStore';
import { useToast } from '@/stores/uiStore';
import { AccessGroupsSection } from './AccessGroupsSection';
import { Button, Modal, StatusBadge } from '@/components/common';
import * as authApi from '@/api/auth';
import * as cloudinaryApi from '@/api/cloudinary';
import * as gtmApi from '@/api/gtm';
import * as klaviyoApi from '@/api/klaviyo';
import * as parcellabApi from '@/api/parcellab';
import * as kodifApi from '@/api/kodif';
import * as notionAiTasksApi from '@/api/notion-ai-tasks';
import * as settingsApi from '@/api/settings';
import { get, post } from '@/api/client';
import { BRANDING, normalizeAssistantDisplayName } from '@/lib/branding';

interface IntegrationField {
  label: string;
  type: 'text' | 'password' | 'textarea';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface IntegrationDef {
  key: string;
  title: string;
  isConfigured: boolean;
  fields: IntegrationField[];
  description?: string;
  docsUrl?: string;
  onSave: () => void;
  onTest?: () => void;
  customContent?: React.ReactNode;
}

interface TeamSettingsProps {
  team: Team;
}

function normalizeOptionalUrl(value: string): string | null {
  const trimmed = value.trim();
  return trimmed || null;
}

function isHttpUrl(value: string): boolean {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function isValidLogoUrl(value: string): boolean {
  return value.startsWith('data:image/') || isHttpUrl(value);
}

/** Matches ShipBob modal placeholder when user saves token without typing a base URL. */
const SHIPBOB_DEFAULT_API_BASE_URL = 'https://api.shipbob.com/1.0';

const EXEC_PULSE_METRIC_FIELDS = [
  ['gross_revenue', 'Gross Revenue'],
  ['shipping_revenue', 'Shipping Revenue'],
  ['discounts', 'Discounts'],
  ['returns', 'Returns'],
  ['expected_returns', 'Expected Returns'],
  ['net_revenue', 'Net Revenue'],
  ['product_cost', 'Product Cost'],
  ['inbound_freight', 'Inbound Freight'],
  ['fulfillment', 'Fulfillment'],
  ['outbound_shipping', 'Outbound Shipping'],
  ['merchant_fees', 'Merchant Fees'],
  ['general_fees', 'General Fees'],
  ['total_cogs', 'Total COGS'],
  ['duties', 'Duties & Tariffs'],
  ['shrinkage', 'Shrinkage'],
  ['marketplace', 'Marketplace Cost'],
  ['gross_profit', 'Gross Profit'],
  ['customer_acquisition', 'Customer Acquisition'],
  ['contribution_profit', 'Contribution Profit'],
  ['net_revenue_budget', 'Net Revenue Budget'],
  ['marketing_budget', 'Marketing Budget'],
  ['marketing_expense', 'Marketing Expense'],
  ['opex', 'Opex'],
  ['payroll', 'Payroll'],
  ['professional_services', 'Professional Services'],
  ['technology', 'Technology'],
  ['general_admin', 'General Admin'],
  ['retail', 'Retail Expense'],
  ['facilities', 'Facilities'],
  ['other_income', 'Other Income'],
  ['interest', 'Interest'],
  ['net_income', 'Net Income'],
  ['ebitda', 'EBITDA'],
  ['ebitda_budget', 'EBITDA Budget'],
  ['dtc_discount_rate', 'DTC Discount Rate'],
  ['channel_dtc_us_net_revenue', 'Channel DTC US Net Revenue'],
  ['channel_dtc_intl_net_revenue', 'Channel DTC Intl Net Revenue'],
  ['channel_marketplace_net_revenue', 'Channel Marketplace Net Revenue'],
  ['channel_wholesale_net_revenue', 'Channel Wholesale Net Revenue'],
  ['channel_retail_net_revenue', 'Channel Retail Net Revenue'],
  ['channel_dtc_us_marketing_spend', 'Channel DTC US Marketing Spend'],
  ['channel_dtc_intl_marketing_spend', 'Channel DTC Intl Marketing Spend'],
  ['channel_marketplace_marketing_spend', 'Channel Marketplace Marketing Spend'],
  ['channel_wholesale_marketing_spend', 'Channel Wholesale Marketing Spend'],
  ['channel_retail_marketing_spend', 'Channel Retail Marketing Spend'],
  ['channel_dtc_us_net_revenue_budget', 'Channel DTC US Net Revenue Budget'],
  ['channel_dtc_intl_net_revenue_budget', 'Channel DTC Intl Net Revenue Budget'],
  ['channel_marketplace_net_revenue_budget', 'Channel Marketplace Net Revenue Budget'],
  ['channel_wholesale_net_revenue_budget', 'Channel Wholesale Net Revenue Budget'],
  ['channel_retail_net_revenue_budget', 'Channel Retail Net Revenue Budget'],
  ['channel_dtc_us_marketing_spend_budget', 'Channel DTC US Marketing Spend Budget'],
  ['channel_dtc_intl_marketing_spend_budget', 'Channel DTC Intl Marketing Spend Budget'],
  ['channel_marketplace_marketing_spend_budget', 'Channel Marketplace Marketing Spend Budget'],
  ['channel_wholesale_marketing_spend_budget', 'Channel Wholesale Marketing Spend Budget'],
  ['channel_retail_marketing_spend_budget', 'Channel Retail Marketing Spend Budget'],
] as const;

function execPulseMetricFieldKey(metric: string): string {
  return `execPulseMetric_${metric}`;
}

export function TeamSettings({ team }: TeamSettingsProps) {
  const setMembers = useTeamStore((state) => state.setMembers);
  const updateTeam = useTeamStore((state) => state.updateTeam);
  const setCurrentTeam = useTeamStore((state) => state.setCurrentTeam);
  const fetchTeams = useAuthStore((state) => state.fetchTeams);
  const members = useTeamStore((state) => state.members);
  const toast = useToast();

  const [logoUrl, setLogoUrl] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'member' | 'admin'>('member');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<string | null>(null);

  // Single state object for all integration form fields
  const [fields, setFields] = useReducer(
    (prev: Record<string, string>, next: Record<string, string>) => ({ ...prev, ...next }),
    {} as Record<string, string>,
  );
  const f = (key: string) => fields[key] ?? '';
  const sf = (key: string) => (value: string) => setFields({ [key]: value });

  // BigQuery picker state (arrays/booleans — kept as separate useState)
  const [bigquerySelectedTables, setBigquerySelectedTables] = useState<string[]>([]);
  const [bigquerySavedTables, setBigquerySavedTables] = useState<string[]>([]);
  const [bigqueryDatasets, setBigqueryDatasets] = useState<string[]>([]);
  const [bigqueryActiveDataset, setBigqueryActiveDataset] = useState<string | null>(null);
  const [bigqueryDatasetTables, setBigqueryDatasetTables] = useState<string[]>([]);
  const [isLoadingDatasets, setIsLoadingDatasets] = useState(false);
  const [isLoadingDatasetTables, setIsLoadingDatasetTables] = useState(false);
  const [datasetSearch, setDatasetSearch] = useState('');
  const [tableSearch, setTableSearch] = useState('');
  const [execPulseSchemaStatus, setExecPulseSchemaStatus] = useState<string | null>(null);

  // Initialize state from team prop data (already in the team response)
  useEffect(() => {
    if (team.logo_url) setLogoUrl(team.logo_url);
    if (team.website_url) setWebsiteUrl(team.website_url);

    const init: Record<string, string> = {};
    if (team.okendo_user_id) init.okendoUserId = team.okendo_user_id;
    if (team.gorgias_email) init.gorgiasEmail = team.gorgias_email;
    if (team.gorgias_base_url) init.gorgiasBaseUrl = team.gorgias_base_url;
    if (team.slack_integration?.workspace_id) init.slackWorkspaceId = team.slack_integration.workspace_id;
    if (team.slack_integration?.workspace_name) init.slackWorkspaceName = team.slack_integration.workspace_name;
    if (team.slack_integration?.channel_id) init.slackChannelId = team.slack_integration.channel_id;
    if (team.slack_integration?.channel_label) init.slackChannel = team.slack_integration.channel_label;
    if (team.shopify_store_domain) init.shopifyDomain = team.shopify_store_domain;
    if (team.cloudinary_cloud_name) init.cloudinaryCloudName = team.cloudinary_cloud_name;
    if (team.gtm_sheet_id) init.gtmSheetId = team.gtm_sheet_id;
    if (team.gtm_sheet_name) init.gtmSheetName = team.gtm_sheet_name;
    if (team.klaviyo_base_url) init.klaviyoBaseUrl = team.klaviyo_base_url;
    init.kodifBaseUrl = team.kodif_base_url || '';
    init.sageBaseUrl = team.sage_base_url || '';
    if (team.sage_team_id) init.sageTeamId = team.sage_team_id;
    if (team.team_priorities_sheet_id) init.teamPrioritiesSheetId = team.team_priorities_sheet_id;
    if (team.team_priorities_sheet_gid !== null && team.team_priorities_sheet_gid !== undefined) {
      init.teamPrioritiesSheetGid = String(team.team_priorities_sheet_gid);
    }
    if (team.notion_ai_tasks_database_id) init.notionAiTasksDatabaseId = team.notion_ai_tasks_database_id;
    if (team.bigquery_project_id) init.bigqueryProjectId = team.bigquery_project_id;
    if (team.bigquery_dataset) init.bigqueryDataset = team.bigquery_dataset;
    if (team.exec_pulse_finance_dataset) init.execPulseFinanceDataset = team.exec_pulse_finance_dataset;
    if (team.exec_pulse_finance_table) init.execPulseFinanceTable = team.exec_pulse_finance_table;
    if (team.exec_pulse_finance_date_column) {
      init.execPulseFinanceDateColumn = team.exec_pulse_finance_date_column;
    }
    if (team.exec_pulse_finance_metric_map) {
      Object.entries(team.exec_pulse_finance_metric_map).forEach(([metric, column]) => {
        init[execPulseMetricFieldKey(metric)] = column;
      });
    }
    if (team.stord_base_url) init.stordBaseUrl = team.stord_base_url;
    if (team.stord_org_id) init.stordOrgId = team.stord_org_id;
    if (team.stord_network_id) init.stordNetworkId = team.stord_network_id;
    if (team.stord_channel_ids?.length) init.stordChannelIds = team.stord_channel_ids.join(', ');
    if (team.stord_statuses?.length) init.stordStatuses = team.stord_statuses.join(', ');
    if (team.shipbob_oos_exception_reasons?.length)
      init.shipbobOosExceptionReasons = team.shipbob_oos_exception_reasons.join(', ');
    if (team.shipbob_base_url) init.shipbobBaseUrl = team.shipbob_base_url;
    if (team.rippling_company_id) init.ripplingCompanyId = team.rippling_company_id;
    init.aiName = normalizeAssistantDisplayName(team.ai_name);
    if (team.ai_avatar_url) init.aiAvatarUrl = team.ai_avatar_url;
    if (team.ai_persona) init.aiPersona = team.ai_persona;
    if (team.ai_best_practices) init.aiBestPractices = team.ai_best_practices;
    setFields(init);

    if (team.bigquery_tables?.length) {
      setBigquerySelectedTables(team.bigquery_tables);
      setBigquerySavedTables(team.bigquery_tables);
    }
  }, [team.id, team.created_at]);

  // Auto-load BigQuery datasets + current selection when modal opens
  useEffect(() => {
    if (selectedIntegration === 'bigquery' && team.bigquery_configured && bigqueryDatasets.length === 0 && !isLoadingDatasets) {
      setIsLoadingDatasets(true);
      // Load datasets and current selection in parallel
      Promise.all([
        settingsApi.listBigQueryDatasets(team.id),
        settingsApi.listBigQueryTables(team.id),
      ])
        .then(([datasetsResult, tablesResult]) => {
          setBigqueryDatasets(datasetsResult.datasets);
          setBigquerySelectedTables(tablesResult.selected);
          setBigquerySavedTables(tablesResult.selected);
        })
        .catch((err) => {
          const error = err as { detail?: string };
          toast.error(error.detail || 'Failed to load datasets');
        })
        .finally(() => setIsLoadingDatasets(false));
    }
    // Reset when modal closes
    if (!selectedIntegration) {
      setBigqueryDatasets([]);
      setBigqueryActiveDataset(null);
      setBigqueryDatasetTables([]);
      setDatasetSearch('');
      setTableSearch('');
    }
  }, [selectedIntegration]);

  // Load members from team prop
  useEffect(() => {
    if (team.members) {
      setMembers(team.members.map(m => ({
        id: m.id,
        user_id: m.id,
        team_id: team.id,
        email: m.email,
        name: m.name,
        picture: m.picture,
        role: (m.role || 'member') as 'admin' | 'member',
        joined_at: '',
        access_group_id: m.access_group_id ?? null,
      })));
    }
  }, [team.id, team.members]);

  const handleSaveBrandAssets = async () => {
    const nextLogoUrl = normalizeOptionalUrl(logoUrl);
    const nextWebsiteUrl = normalizeOptionalUrl(websiteUrl);

    if (nextLogoUrl && !isValidLogoUrl(nextLogoUrl)) {
      toast.error('Logo URL must be an HTTP(S) URL or data URI');
      return;
    }

    if (nextWebsiteUrl && !isHttpUrl(nextWebsiteUrl)) {
      toast.error('Website URL must be a valid HTTP(S) URL');
      return;
    }

    setIsLoading(true);
    try {
      const updatedTeam = await post<Team>(`/api/teams/${team.id}/logo`, {
        logo_url: nextLogoUrl,
        website_url: nextWebsiteUrl,
      });
      updateTeam(updatedTeam);
      setLogoUrl(updatedTeam.logo_url || '');
      setWebsiteUrl(updatedTeam.website_url || '');
      toast.success('Brand assets saved successfully!');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save brand assets');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInviteMember = async () => {
    if (!inviteEmail.trim()) return;

    setIsLoading(true);
    try {
      await authApi.inviteTeamMember(team.id, inviteEmail.trim(), inviteRole);
      toast.success('Invitation sent!');
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('member');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to send invitation');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveOkendo = async () => {
    setIsLoading(true);
    try {
      await post(`/api/teams/${team.id}/okendo/credentials`, {
        api_key: f('okendoApiKey') || undefined,
        user_id: f('okendoUserId') || undefined,
      });
      toast.success('Okendo settings saved!');
      sf('okendoApiKey')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Okendo settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveFlip = async () => {
    setIsLoading(true);
    try {
      await settingsApi.saveFlipConfig(team.id, {
        api_key: f('flipApiKey') || undefined,
      });
      toast.success('Flip CX settings saved!');
      sf('flipApiKey')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Flip CX settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveGorgias = async () => {
    setIsLoading(true);
    try {
      await settingsApi.saveGorgiasConfig(team.id, {
        api_key: f('gorgiasApiKey') || undefined,
        email: f('gorgiasEmail') || undefined,
        base_url: f('gorgiasBaseUrl') || undefined,
      });
      toast.success('Gorgias settings saved!');
      sf('gorgiasApiKey')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Gorgias settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveSlack = async () => {
    setIsLoading(true);
    try {
      await post(`/api/teams/${team.id}/slack`, {
        slack_workspace_id: f('slackWorkspaceId') || undefined,
        workspace_name: f('slackWorkspaceName') || undefined,
        bot_token: f('slackBotToken') || undefined,
        channel_id: f('slackChannelId') || undefined,
        channel_label: f('slackChannel') || undefined,
      });
      toast.success('Slack settings saved!');
      sf('slackBotToken')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Slack settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveShopify = async () => {
    setIsLoading(true);
    try {
      await post(`/api/teams/${team.id}/shopify/api`, {
        store_domain: f('shopifyDomain') || undefined,
        access_token: f('shopifyToken') || undefined,
      });
      toast.success('Shopify settings saved!');
      sf('shopifyToken')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Shopify settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveCloudinary = async () => {
    setIsLoading(true);
    try {
      await cloudinaryApi.updateCloudinaryCredentials(team.id, {
        cloud_name: f('cloudinaryCloudName') || undefined,
        api_key: f('cloudinaryApiKey') || undefined,
        api_secret: f('cloudinaryApiSecret') || undefined,
        enable_sync: true,
      });
      toast.success('Cloudinary settings saved!');
      setFields({ cloudinaryApiKey: '', cloudinaryApiSecret: '' });
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Cloudinary settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestCloudinary = async () => {
    try {
      const result = await cloudinaryApi.testCloudinaryConnection(team.id);
      toast.success(`Connected to Cloudinary (${result.cloud_name})`);
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveGtm = async () => {
    setIsLoading(true);
    try {
      await gtmApi.updateGtmCredentials(team.id, {
        credentials_json: f('gtmCredentialsJson') || undefined,
        sheet_id: f('gtmSheetId') || undefined,
        sheet_name: f('gtmSheetName') || undefined,
      });
      toast.success('GTM settings saved!');
      sf('gtmCredentialsJson')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save GTM settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestGtm = async () => {
    try {
      const result = await gtmApi.testGtmConnection(team.id);
      toast.success(`Connected to "${result.title}" (${result.sheet_count} sheets)`);
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveKlaviyo = async () => {
    setIsLoading(true);
    try {
      await klaviyoApi.updateKlaviyoCredentials(team.id, {
        api_key: f('klaviyoApiKey') || undefined,
        base_url: f('klaviyoBaseUrl') || undefined,
      });
      toast.success('Klaviyo settings saved!');
      sf('klaviyoApiKey')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Klaviyo settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestKlaviyo = async () => {
    try {
      const result = await klaviyoApi.testKlaviyoConnection(team.id);
      if (result.connected) {
        toast.success(`Connected to Klaviyo (sample rows: ${result.sample_count ?? 0})`);
      } else {
        toast.error('Klaviyo connection test failed');
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveParcelLab = async () => {
    setIsLoading(true);
    try {
      await parcellabApi.updateParcelLabCredentials(team.id, {
        account_id: f('parcellabAccountId') || undefined,
        api_token: f('parcellabApiToken') || undefined,
        base_url: f('parcellabBaseUrl') || undefined,
      });
      toast.success('ParcelLab settings saved!');
      sf('parcellabApiToken')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save ParcelLab settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestParcelLab = async () => {
    try {
      const result = await parcellabApi.testParcelLabConnection(team.id);
      if (result.connected && result.read_orders) {
        toast.success('Connected to ParcelLab with order read access');
      } else {
        toast.error('ParcelLab order read access is unavailable');
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveBigQuery = async () => {
    setIsLoading(true);
    try {
      const execPulseFinanceMetricMap = EXEC_PULSE_METRIC_FIELDS.reduce<Record<string, string>>(
        (acc, [metric]) => {
          const column = f(execPulseMetricFieldKey(metric)).trim();
          if (column) acc[metric] = column;
          return acc;
        },
        {},
      );
      const hasMetricOverrides = Object.keys(execPulseFinanceMetricMap).length > 0;
      const shouldSendMetricMap = hasMetricOverrides || !!team.exec_pulse_finance_metric_map;

      if (hasMetricOverrides) {
        const invalidOverride = Object.values(execPulseFinanceMetricMap).find(
          (column) => !/^[A-Za-z_][A-Za-z0-9_]*$/.test(column),
        );
        if (invalidOverride) {
          toast.error('Metric override column names can only use letters, numbers, and underscores.');
          return;
        }
      }

      await settingsApi.saveBigQueryConfig(team.id, {
        project_id: f('bigqueryProjectId') || undefined,
        credentials_json: f('bigqueryCredentialsJson') || undefined,
        dataset: f('bigqueryDataset') || undefined,
        exec_pulse_finance_dataset: f('execPulseFinanceDataset'),
        exec_pulse_finance_table: f('execPulseFinanceTable'),
        exec_pulse_finance_date_column: f('execPulseFinanceDateColumn'),
        exec_pulse_finance_metric_map: shouldSendMetricMap ? execPulseFinanceMetricMap : undefined,
      });
      toast.success('BigQuery settings saved!');
      sf('bigqueryCredentialsJson')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save BigQuery settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveKodif = async () => {
    setIsLoading(true);
    try {
      await kodifApi.updateKodifCredentials(team.id, {
        api_key: f('kodifApiKey') || undefined,
        base_url: f('kodifBaseUrl') || undefined,
      });
      toast.success('Kodif settings saved!');
      sf('kodifApiKey')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Kodif settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestKodif = async () => {
    try {
      const result = await kodifApi.testKodifConnection(team.id);
      if (result.connected) {
        toast.success(`Connected to Kodif (sample rows: ${result.sample_count ?? 0})`);
      } else {
        toast.error('Kodif test failed');
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveSage = async () => {
    setIsLoading(true);
    try {
      const result = await settingsApi.saveSageConfig(team.id, {
        api_key: f('sageApiKey') || undefined,
        team_id: f('sageTeamId') || undefined,
        base_url: f('sageBaseUrl') || undefined,
      });
      toast.success('Sage settings saved!');
      updateTeam({
        ...team,
        sage_configured: result.sage_configured,
        sage_base_url: result.sage_base_url,
        sage_team_id: result.sage_team_id,
        sage_last_synced_at: result.sage_last_synced_at,
      });
      sf('sageApiKey')('');
      if (result.sage_base_url) sf('sageBaseUrl')(result.sage_base_url);
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Sage settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestSage = async () => {
    try {
      const result = await settingsApi.testSageConnection(team.id);
      if (result.connected) {
        toast.success(`Connected to Sage (team ${result.sage_team_id})`);
      } else {
        toast.error('Sage test failed');
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveTeamPriorities = async () => {
    const trimmedSheetId = f('teamPrioritiesSheetId').trim();
    const trimmedSheetGid = f('teamPrioritiesSheetGid').trim();

    if (!trimmedSheetId || !trimmedSheetGid) {
      toast.error('Team Priorities Sheet ID and GID are required');
      return;
    }

    const parsedGid = Number(trimmedSheetGid);
    if (!Number.isInteger(parsedGid) || parsedGid < 0) {
      toast.error('Team Priorities Sheet GID must be a non-negative integer');
      return;
    }

    setIsLoading(true);
    try {
      await post(`/api/teams/${team.id}/team-priorities/settings`, {
        sheet_id: trimmedSheetId,
        sheet_gid: parsedGid,
      });
      toast.success('Team Priorities settings saved!');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Team Priorities settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveNotionAiTasks = async () => {
    setIsLoading(true);
    try {
      await notionAiTasksApi.saveNotionAiTasksSettings(team.id, {
        database_id: f('notionAiTasksDatabaseId') || undefined,
        api_token: f('notionAiTasksApiToken') || undefined,
      });
      toast.success('Notion AI Tasks settings saved!');
      sf('notionAiTasksApiToken')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Notion AI Tasks settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestNotionAiTasks = async () => {
    try {
      const result = await notionAiTasksApi.testNotionAiTasks(team.id);
      toast.success(`Connected to ${result.title || 'Notion database'}`);
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Notion connection test failed');
    }
  };

  const handleSyncNotionAiTasks = async () => {
    setIsLoading(true);
    try {
      const result = await notionAiTasksApi.syncNotionAiTasks(team.id);
      toast.success(`Notion sync complete: ${result.fetched} fetched, ${result.created} created`);
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Notion sync failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestBigQuery = async () => {
    try {
      const result = await settingsApi.testBigQueryConnection(team.id);
      if (result.success) {
        toast.success(result.message);
      } else {
        toast.error(result.message);
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleValidateExecPulseFinanceSchema = async () => {
    setIsLoading(true);
    setExecPulseSchemaStatus(null);
    try {
      const schema = await settingsApi.getExecPulseFinanceSchema(team.id);
      const mappedCount = Object.keys(schema.mapped_metrics).length;
      const missingCount = schema.missing_metrics.length;
      setExecPulseSchemaStatus(
        `${schema.dataset}.${schema.table}: ${mappedCount} metrics mapped, ${missingCount} optional gaps, date column ${schema.date_column || 'not found'}.`,
      );
      if (schema.date_column && mappedCount > 0) {
        toast.success('Weekly P&L source validated.');
      } else {
        toast.error('Weekly P&L source is reachable but missing required date or metric columns.');
      }
    } catch (err) {
      const error = err as { detail?: string };
      const message = error.detail || 'Failed to validate Weekly P&L source';
      setExecPulseSchemaStatus(message);
      toast.error(message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectBigQueryDataset = (dataset: string) => {
    setBigqueryActiveDataset(dataset);
    setBigqueryDatasetTables([]);
    setTableSearch('');
    setIsLoadingDatasetTables(true);
    settingsApi.listBigQueryDatasetTables(team.id, dataset)
      .then((result) => {
        setBigqueryDatasetTables(result.tables);
      })
      .catch((err) => {
        const error = err as { detail?: string };
        toast.error(error.detail || 'Failed to load tables');
      })
      .finally(() => setIsLoadingDatasetTables(false));
  };

  const handleBackToDatasets = () => {
    setBigqueryActiveDataset(null);
    setBigqueryDatasetTables([]);
    setTableSearch('');
  };

  const handleToggleBigQueryTable = (tableId: string) => {
    const fullName = `${bigqueryActiveDataset}.${tableId}`;
    setBigquerySelectedTables((prev) => {
      if (prev.includes(fullName)) {
        return prev.filter((t) => t !== fullName);
      }
      if (prev.length >= 20) {
        toast.error('Maximum 20 tables allowed');
        return prev;
      }
      return [...prev, fullName];
    });
  };

  const handleRemoveBigQueryTable = (tableName: string) => {
    setBigquerySelectedTables((prev) => prev.filter((t) => t !== tableName));
  };

  const handleSaveBigQueryTables = async () => {
    setIsLoading(true);
    try {
      await settingsApi.saveBigQueryConfig(team.id, {
        tables: bigquerySelectedTables,
      });
      setBigquerySavedTables([...bigquerySelectedTables]);
      const updated = bigquerySelectedTables.length > 0 ? bigquerySelectedTables : null;
      toast.success(`Table selection saved (${bigquerySelectedTables.length} tables)`);
      updateTeam({ ...team, bigquery_tables: updated });
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save table selection');
    } finally {
      setIsLoading(false);
    }
  };

  const bigqueryTablesHaveChanges = (() => {
    if (bigquerySelectedTables.length !== bigquerySavedTables.length) return true;
    const sorted1 = [...bigquerySelectedTables].sort();
    const sorted2 = [...bigquerySavedTables].sort();
    return sorted1.some((t, i) => t !== sorted2[i]);
  })();

  const parseCsv = (value: string): string[] =>
    value
      .split(',')
      .map((part) => part.trim())
      .filter(Boolean);

  const handleSaveStord = async () => {
    setIsLoading(true);
    try {
      await post(`/api/teams/${team.id}/stord/credentials`, {
        base_url: f('stordBaseUrl') || undefined,
        api_token: f('stordApiToken') || undefined,
        org_id: f('stordOrgId') || undefined,
        network_id: f('stordNetworkId') || undefined,
        channel_ids: f('stordChannelIds').trim() ? parseCsv(f('stordChannelIds')) : undefined,
        statuses: f('stordStatuses').trim() ? parseCsv(f('stordStatuses')) : undefined,
      });
      toast.success('Stord settings saved!');
      sf('stordApiToken')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Stord settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestStord = async () => {
    try {
      const result = await get<{ status: string; connected: boolean; sample_count?: number }>(
        `/api/teams/${team.id}/stord/test`
      );
      if (result.connected) {
        toast.success(`Connected to Stord (sample rows: ${result.sample_count ?? 0})`);
      } else {
        toast.error('Stord test failed');
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveShipbob = async () => {
    setIsLoading(true);
    try {
      const rawBase = f('shipbobBaseUrl').trim();
      const rawToken = f('shipbobApiToken').trim();
      const base_url =
        rawBase || (rawToken ? SHIPBOB_DEFAULT_API_BASE_URL : undefined);

      const result = await post<{
        shipbob_configured: boolean;
        shipbob_base_url: string | null;
        shipbob_api_token_preview?: string | null;
        shipbob_oos_exception_reasons?: string[] | null;
      }>(`/api/teams/${team.id}/shipbob/credentials`, {
        base_url: base_url || undefined,
        api_token: rawToken || undefined,
        // Omitted when blank so saving credentials never silently resets the
        // reason filter. The backend rejects unknown reasons with a 422.
        oos_exception_reasons: f('shipbobOosExceptionReasons').trim()
          ? parseCsv(f('shipbobOosExceptionReasons'))
          : undefined,
      });
      toast.success('ShipBob settings saved!');
      updateTeam({
        ...team,
        shipbob_configured: result.shipbob_configured,
        shipbob_base_url: result.shipbob_base_url ?? null,
        shipbob_api_token_preview: result.shipbob_api_token_preview ?? null,
      });
      await fetchTeams();
      const fresh = useAuthStore.getState().teams.find((t) => t.id === team.id);
      if (fresh) setCurrentTeam(fresh);
      sf('shipbobApiToken')('');
      if (!rawBase && result.shipbob_base_url) {
        sf('shipbobBaseUrl')(result.shipbob_base_url);
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save ShipBob settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRippling = async () => {
    setIsLoading(true);
    try {
      await settingsApi.saveRipplingConfig(team.id, {
        api_key: f('ripplingApiKey') || undefined,
        company_id: f('ripplingCompanyId') || undefined,
      });
      toast.success('Rippling settings saved!');
      sf('ripplingApiKey')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Rippling settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTestShipbob = async () => {
    try {
      const result = await get<{ status: string; connected: boolean; sample_count?: number }>(
        `/api/teams/${team.id}/shipbob/test`
      );
      if (result.connected) {
        toast.success(`Connected to ShipBob (sample rows: ${result.sample_count ?? 0})`);
      } else {
        toast.error('ShipBob test failed');
      }
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Connection test failed');
    }
  };

  const handleSaveTripleWhale = async () => {
    setIsLoading(true);
    try {
      const result = await settingsApi.saveTripleWhaleConfig(team.id, {
        api_key: f('triplewhaleApiKey') || undefined,
      });
      toast.success('Triple Whale settings saved!');
      updateTeam({
        ...team,
        triplewhale_configured: result.triplewhale_configured,
        triplewhale_api_key_preview: result.triplewhale_api_key_preview ?? null,
      });
      await fetchTeams();
      const fresh = useAuthStore.getState().teams.find((t) => t.id === team.id);
      if (fresh) setCurrentTeam(fresh);
      sf('triplewhaleApiKey')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Triple Whale settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveRamp = async () => {
    setIsLoading(true);
    try {
      const result = await settingsApi.saveRampConfig(team.id, {
        api_key: f('rampApiKey') || undefined,
        client_id: f('rampClientId') || undefined,
        client_secret: f('rampClientSecret') || undefined,
      });
      toast.success('Ramp settings saved!');
      updateTeam({
        ...team,
        ramp_configured: result.ramp_configured,
        ramp_api_key_preview: result.ramp_api_key_preview ?? null,
        ramp_client_id_preview: result.ramp_client_id_preview ?? null,
        ramp_client_secret_preview: result.ramp_client_secret_preview ?? null,
      });
      await fetchTeams();
      const fresh = useAuthStore.getState().teams.find((t) => t.id === team.id);
      if (fresh) setCurrentTeam(fresh);
      sf('rampApiKey')('');
      sf('rampClientId')('');
      sf('rampClientSecret')('');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save Ramp settings');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveAiPersona = async () => {
    setIsLoading(true);
    try {
      await settingsApi.saveAiPersona(team.id, {
        name: f('aiName') || undefined,
        avatar_url: f('aiAvatarUrl') || undefined,
        persona: f('aiPersona') || undefined,
        best_practices: f('aiBestPractices') || undefined,
      });
      updateTeam({
        ai_name: f('aiName') || null,
        ai_avatar_url: f('aiAvatarUrl') || null,
        ai_persona: f('aiPersona') || null,
        ai_best_practices: f('aiBestPractices') || null,
      });
      toast.success('AI persona saved!');
    } catch (err) {
      const error = err as { detail?: string };
      toast.error(error.detail || 'Failed to save AI persona');
    } finally {
      setIsLoading(false);
    }
  };

  const integrations: IntegrationDef[] = [
    {
      key: 'okendo',
      title: 'Okendo',
      isConfigured: team.okendo_configured,
      fields: [
        { label: 'API Key', type: 'password', value: f('okendoApiKey'), onChange: sf('okendoApiKey'), placeholder: team.okendo_api_key_preview || 'Enter API key' },
        { label: 'User ID', type: 'text', value: f('okendoUserId'), onChange: sf('okendoUserId'), placeholder: 'Enter User ID' },
      ],
      onSave: handleSaveOkendo,
    },
    {
      key: 'gorgias',
      title: 'Gorgias',
      isConfigured: team.gorgias_configured,
      fields: [
        { label: 'API Key', type: 'password', value: f('gorgiasApiKey'), onChange: sf('gorgiasApiKey'), placeholder: team.gorgias_api_key_preview || 'Enter API key' },
        { label: 'Email', type: 'text', value: f('gorgiasEmail'), onChange: sf('gorgiasEmail'), placeholder: 'admin@company.com' },
        { label: 'Subdomain URL', type: 'text', value: f('gorgiasBaseUrl'), onChange: sf('gorgiasBaseUrl'), placeholder: 'https://yourcompany.gorgias.com' },
      ],
      onSave: handleSaveGorgias,
    },
    {
      key: 'flip',
      title: 'Flip CX',
      isConfigured: team.flip_configured,
      fields: [
        { label: 'API Key', type: 'password', value: f('flipApiKey'), onChange: sf('flipApiKey'), placeholder: team.flip_api_key_preview || 'ak_...' },
      ],
      onSave: handleSaveFlip,
    },
    {
      key: 'triplewhale',
      title: 'Triple Whale (Marketing Attribution)',
      isConfigured: team.triplewhale_configured,
      fields: [
        { label: 'API Key', type: 'password', value: f('triplewhaleApiKey'), onChange: sf('triplewhaleApiKey'), placeholder: team.triplewhale_api_key_preview || 'Enter API key' },
      ],
      onSave: handleSaveTripleWhale,
    },
    {
      key: 'ramp',
      title: 'Ramp',
      isConfigured: !!team.ramp_configured,
      fields: [
        { label: 'Client ID', type: 'password', value: f('rampClientId'), onChange: sf('rampClientId'), placeholder: team.ramp_client_id_preview || 'ramp_id_...' },
        { label: 'Client Secret', type: 'password', value: f('rampClientSecret'), onChange: sf('rampClientSecret'), placeholder: team.ramp_client_secret_preview || 'ramp_sec_...' },
        { label: 'Legacy API Token', type: 'password', value: f('rampApiKey'), onChange: sf('rampApiKey'), placeholder: team.ramp_api_key_preview || 'Optional bearer token' },
      ],
      onSave: handleSaveRamp,
    },
    {
      key: 'slack',
      title: 'Slack',
      isConfigured: team.slack_configured,
      fields: [
        { label: 'Workspace ID', type: 'text', value: f('slackWorkspaceId'), onChange: sf('slackWorkspaceId'), placeholder: 'T0123456789' },
        { label: 'Workspace Name', type: 'text', value: f('slackWorkspaceName'), onChange: sf('slackWorkspaceName'), placeholder: 'True Classic' },
        { label: 'Bot Token', type: 'password', value: f('slackBotToken'), onChange: sf('slackBotToken'), placeholder: 'xoxb-...' },
        { label: 'Channel ID', type: 'text', value: f('slackChannelId'), onChange: sf('slackChannelId'), placeholder: 'C0123456789' },
        { label: 'Channel Label', type: 'text', value: f('slackChannel'), onChange: sf('slackChannel'), placeholder: '#customer-feedback' },
      ],
      onSave: handleSaveSlack,
    },
    {
      key: 'shopify',
      title: 'Shopify',
      isConfigured: team.shopify_configured,
      fields: [
        { label: 'Store Domain', type: 'text', value: f('shopifyDomain'), onChange: sf('shopifyDomain'), placeholder: 'your-store.myshopify.com' },
        { label: 'Access Token', type: 'password', value: f('shopifyToken'), onChange: sf('shopifyToken'), placeholder: team.shopify_access_token_preview || 'shpat_xxx...' },
      ],
      onSave: handleSaveShopify,
    },
    {
      key: 'cloudinary',
      title: 'Cloudinary',
      isConfigured: team.cloudinary_configured,
      fields: [
        { label: 'Cloud Name', type: 'text', value: f('cloudinaryCloudName'), onChange: sf('cloudinaryCloudName'), placeholder: 'your-cloud-name' },
        { label: 'API Key', type: 'password', value: f('cloudinaryApiKey'), onChange: sf('cloudinaryApiKey'), placeholder: team.cloudinary_api_key_preview || 'Enter API key' },
        { label: 'API Secret', type: 'password', value: f('cloudinaryApiSecret'), onChange: sf('cloudinaryApiSecret'), placeholder: team.cloudinary_api_secret_preview || 'Enter API secret' },
      ],
      onSave: handleSaveCloudinary,
      onTest: team.cloudinary_configured ? handleTestCloudinary : undefined,
    },
    {
      key: 'gtm',
      title: 'GTM (Google Sheets)',
      isConfigured: team.gtm_configured,
      fields: [
        { label: 'Service Account JSON', type: 'textarea', value: f('gtmCredentialsJson'), onChange: sf('gtmCredentialsJson'), placeholder: team.gtm_configured ? '••• credentials saved •••' : 'Paste service account JSON here' },
        { label: 'Sheet ID', type: 'text', value: f('gtmSheetId'), onChange: sf('gtmSheetId'), placeholder: 'e.g. 1BxiMVs0XRA5nFMdKvBdBZjgmUUqptlbs74OgVE2upms' },
        { label: 'Sheet Name (tab)', type: 'text', value: f('gtmSheetName'), onChange: sf('gtmSheetName'), placeholder: team.gtm_sheet_name || 'GTM' },
      ],
      onSave: handleSaveGtm,
      onTest: team.gtm_configured ? handleTestGtm : undefined,
    },
    {
      key: 'klaviyo',
      title: 'Klaviyo',
      isConfigured: !!team.klaviyo_configured,
      fields: [
        { label: 'Base URL', type: 'text', value: f('klaviyoBaseUrl'), onChange: sf('klaviyoBaseUrl'), placeholder: team.klaviyo_base_url || 'https://a.klaviyo.com' },
        { label: 'Private API Key', type: 'password', value: f('klaviyoApiKey'), onChange: sf('klaviyoApiKey'), placeholder: team.klaviyo_api_key_preview || 'pk_...' },
      ],
      onSave: handleSaveKlaviyo,
      onTest: team.klaviyo_configured ? handleTestKlaviyo : undefined,
    },
    {
      key: 'parcellab',
      title: 'ParcelLab',
      isConfigured: !!team.parcellab_configured,
      description: 'Delivery checkpoints and proof of delivery for chargeback evidence.',
      docsUrl: 'https://docs.parcellab.com/docs/developers/orders/trackings-orders',
      fields: [
        { label: 'Account ID', type: 'text', value: f('parcellabAccountId'), onChange: sf('parcellabAccountId'), placeholder: team.parcellab_account_id || 'ParcelLab account ID' },
        { label: 'API Base URL', type: 'text', value: f('parcellabBaseUrl'), onChange: sf('parcellabBaseUrl'), placeholder: team.parcellab_base_url || 'https://api.parcellab.com' },
        { label: 'Read API Token', type: 'password', value: f('parcellabApiToken'), onChange: sf('parcellabApiToken'), placeholder: team.parcellab_api_token_preview || 'Enter company-owned token' },
      ],
      onSave: handleSaveParcelLab,
      onTest: team.parcellab_configured ? handleTestParcelLab : undefined,
    },
    {
      key: 'kodif',
      title: 'Kodif',
      isConfigured: !!team.kodif_configured,
      description: 'Export API uses Authorization header format: Token {API token}.',
      docsUrl: 'https://docs.kodif.ai/integrations/export-api',
      fields: [
        { label: 'Base URL', type: 'text', value: f('kodifBaseUrl'), onChange: sf('kodifBaseUrl'), placeholder: team.kodif_base_url || 'https://gateway.kodif.io/integrations' },
        { label: 'API Token', type: 'password', value: f('kodifApiKey'), onChange: sf('kodifApiKey'), placeholder: team.kodif_api_key_preview || 'Enter Kodif API token' },
      ],
      onSave: handleSaveKodif,
      onTest: team.kodif_configured ? handleTestKodif : undefined,
    },
    {
      key: 'sage',
      title: 'Sage',
      isConfigured: !!team.sage_configured,
      description: 'Sage Supply Chain Intelligence API uses your Anvyl team ID and API key.',
      docsUrl: 'https://developer.anvyl.com/docs/generating-an-api-key',
      fields: [
        { label: 'Base URL', type: 'text', value: f('sageBaseUrl'), onChange: sf('sageBaseUrl'), placeholder: team.sage_base_url || 'https://api.anvyl.com' },
        { label: 'Team ID', type: 'text', value: f('sageTeamId'), onChange: sf('sageTeamId'), placeholder: team.sage_team_id || '578' },
        { label: 'API Key', type: 'password', value: f('sageApiKey'), onChange: sf('sageApiKey'), placeholder: team.sage_api_key_preview || 'Enter Sage API key' },
      ],
      onSave: handleSaveSage,
      onTest: team.sage_configured ? handleTestSage : undefined,
    },
    {
      key: 'bigquery',
      title: 'BigQuery',
      isConfigured: team.bigquery_configured,
      fields: [
        { label: 'GCP Project ID', type: 'text', value: f('bigqueryProjectId'), onChange: sf('bigqueryProjectId'), placeholder: 'your-gcp-project-id' },
        { label: 'Service Account JSON', type: 'textarea', value: f('bigqueryCredentialsJson'), onChange: sf('bigqueryCredentialsJson'), placeholder: team.bigquery_configured ? '••• credentials saved •••' : 'Paste service account JSON here' },
        { label: 'Default Dataset', type: 'text', value: f('bigqueryDataset'), onChange: sf('bigqueryDataset'), placeholder: 'analytics_dataset' },
        { label: 'Weekly P&L Dataset', type: 'text', value: f('execPulseFinanceDataset'), onChange: sf('execPulseFinanceDataset'), placeholder: 'prod_mstr_view' },
        { label: 'Weekly P&L Table', type: 'text', value: f('execPulseFinanceTable'), onChange: sf('execPulseFinanceTable'), placeholder: 'daily_performance_looker_all_final' },
        { label: 'Weekly P&L Date Column', type: 'text', value: f('execPulseFinanceDateColumn'), onChange: sf('execPulseFinanceDateColumn'), placeholder: 'date' },
      ],
      onSave: handleSaveBigQuery,
      onTest: team.bigquery_configured ? handleTestBigQuery : undefined,
      customContent: (
        <>
        <div className="bq-metric-mapper">
          <div className="bq-table-picker__header">
            <label className="form-label bq-table-picker__label">Weekly P&L Metric Overrides</label>
            <span className="form-help bq-table-picker__meta">Optional</span>
          </div>
          <div className="form-help bq-table-picker__hint">
            Leave blank to auto-detect the original Weekly Exec Pulse columns from daily_performance_looker_all_final.
          </div>
          {team.bigquery_configured && (
            <div className="bq-metric-mapper__actions">
              <Button
                variant="outline"
                size="sm"
                onClick={handleValidateExecPulseFinanceSchema}
                isLoading={isLoading}
              >
                Validate Weekly P&L Source
              </Button>
              {execPulseSchemaStatus && (
                <span className="form-help bq-metric-mapper__status">{execPulseSchemaStatus}</span>
              )}
            </div>
          )}
          <div className="bq-metric-mapper__grid">
            {EXEC_PULSE_METRIC_FIELDS.map(([metric, label]) => (
              <div key={metric} className="bq-metric-mapper__field">
                <label className="form-label">{label}</label>
                <input
                  type="text"
                  className="form-input"
                  value={f(execPulseMetricFieldKey(metric))}
                  onChange={(e) => sf(execPulseMetricFieldKey(metric))(e.target.value)}
                  placeholder={metric === 'shipping_revenue' ? 'shipping_income' : metric}
                />
              </div>
            ))}
          </div>
        </div>

        {team.bigquery_configured ? (
          <div className="bq-table-picker">
          <div className="bq-table-picker__header">
            <label className="form-label bq-table-picker__label">
              Table Allowlist
              {bigqueryTablesHaveChanges && (
                <span className="bq-table-picker__unsaved"> (unsaved changes)</span>
              )}
            </label>
            <span className="form-help bq-table-picker__meta">
              {bigquerySelectedTables.length}/20 selected
            </span>
          </div>
          <div className="form-help bq-table-picker__hint">
            Select up to 20 tables for deep schema discovery in the AI prompt. Unselected tables remain queryable at runtime.
          </div>

          {/* Selected tables as removable pills */}
          {bigquerySelectedTables.length > 0 && (
            <div className="bq-table-picker__pills">
              {bigquerySelectedTables.map((table) => (
                <span key={table} className="bq-table-token">
                  <StatusBadge label={table} tone="info" size="sm" />
                  <button
                    type="button"
                    className="bq-table-token__remove"
                    onClick={() => handleRemoveBigQueryTable(table)}
                    aria-label={`Remove ${table}`}
                  >
                    &times;
                  </button>
                </span>
              ))}
            </div>
          )}

          {/* Loading datasets */}
          {isLoadingDatasets && (
            <div className="bq-table-picker__list-section">
              <div className="bq-table-picker__list">
                {Array.from({ length: 4 }, (_, i) => (
                  <div key={i} className="bq-dataset-item bq-dataset-item--loading">
                    <div className="skeleton skeleton--line bq-skeleton-line" style={{ width: `${60 - i * 8}%` }} />
                    <div className="skeleton skeleton--line bq-skeleton-count" />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Two-level browse panel */}
          {bigqueryDatasets.length > 0 && !bigqueryActiveDataset && (
            <div className="bq-table-picker__list-section">
              {bigqueryDatasets.length > 8 && (
                <input
                  type="text"
                  className="form-input bq-table-picker__search"
                  placeholder="Search datasets..."
                  value={datasetSearch}
                  onChange={(e) => setDatasetSearch(e.target.value)}
                  onKeyDown={(e) => e.stopPropagation()}
                  autoComplete="off"
                />
              )}
              <div className="bq-table-picker__list">
                {bigqueryDatasets
                  .filter((d) => !datasetSearch || d.toLowerCase().includes(datasetSearch.toLowerCase()))
                  .map((dataset) => (
                    <button
                      key={dataset}
                      type="button"
                      className="bq-dataset-item"
                      onClick={() => handleSelectBigQueryDataset(dataset)}
                    >
                      <span className="bq-table-picker__name">{dataset}</span>
                      <span className="bq-dataset-item__chevron">&rsaquo;</span>
                    </button>
                  ))}
                {bigqueryDatasets.filter((d) => !datasetSearch || d.toLowerCase().includes(datasetSearch.toLowerCase())).length === 0 && (
                  <div className="bq-table-picker__empty">No datasets match &ldquo;{datasetSearch}&rdquo;</div>
                )}
              </div>
            </div>
          )}

          {/* Drilled-in: tables within a dataset */}
          {bigqueryActiveDataset && (
            <div className="bq-table-picker__list-section">
              <Button type="button" variant="text" size="sm" onClick={handleBackToDatasets}>
                &larr; {bigqueryActiveDataset}
              </Button>

              {isLoadingDatasetTables && (
                <div className="bq-table-picker__list">
                  {Array.from({ length: 5 }, (_, i) => (
                    <div key={i} className="bq-table-picker__row bq-table-picker__row--loading">
                      <div className="skeleton skeleton--line bq-skeleton-checkbox" />
                      <div className="skeleton skeleton--line bq-skeleton-line" style={{ width: `${70 - i * 10}%` }} />
                    </div>
                  ))}
                </div>
              )}

              {!isLoadingDatasetTables && bigqueryDatasetTables.length > 0 && (
                <>
                  {bigqueryDatasetTables.length > 8 && (
                    <input
                      type="text"
                      className="form-input bq-table-picker__search"
                      placeholder="Search tables..."
                      value={tableSearch}
                      onChange={(e) => setTableSearch(e.target.value)}
                      onKeyDown={(e) => e.stopPropagation()}
                      autoComplete="off"
                    />
                  )}
                  <div className="bq-table-picker__list">
                    {bigqueryDatasetTables
                      .filter((t) => !tableSearch || t.toLowerCase().includes(tableSearch.toLowerCase()))
                      .map((tableId) => {
                        const fullName = `${bigqueryActiveDataset}.${tableId}`;
                        const isSelected = bigquerySelectedTables.includes(fullName);
                        return (
                          <button
                            key={tableId}
                            type="button"
                            className={`bq-table-picker__row${isSelected ? ' bq-table-picker__row--selected' : ''}`}
                            onClick={() => handleToggleBigQueryTable(tableId)}
                          >
                            <span className="bq-table-picker__check">{isSelected ? '\u2713' : ''}</span>
                            <span className="bq-table-picker__name">{tableId}</span>
                          </button>
                        );
                      })}
                    {bigqueryDatasetTables.filter((t) => !tableSearch || t.toLowerCase().includes(tableSearch.toLowerCase())).length === 0 && (
                      <div className="bq-table-picker__empty">No tables match &ldquo;{tableSearch}&rdquo;</div>
                    )}
                  </div>
                </>
              )}

              {!isLoadingDatasetTables && bigqueryDatasetTables.length === 0 && (
                <div className="bq-table-picker__empty">No tables found in this dataset.</div>
              )}
            </div>
          )}

          {!isLoadingDatasets && bigqueryDatasets.length === 0 && (
            <div className="bq-table-picker__empty">No datasets found.</div>
          )}

          {/* Save button — only visible when there are unsaved changes */}
          {bigqueryTablesHaveChanges && (
            <div className="bq-table-picker__actions">
              <Button
                size="sm"
                onClick={handleSaveBigQueryTables}
                isLoading={isLoading}
              >
                Save Table Selection
              </Button>
            </div>
          )}
          </div>
        ) : null}
        </>
      ),
    },
    {
      key: 'team-priorities',
      title: 'Team Priorities (Slack Form)',
      isConfigured: !!team.team_priorities_configured,
      fields: [
        { label: 'Responses Sheet ID', type: 'text', value: f('teamPrioritiesSheetId'), onChange: sf('teamPrioritiesSheetId'), placeholder: 'e.g. 1abcDEF...' },
        { label: 'Responses Sheet GID', type: 'text', value: f('teamPrioritiesSheetGid'), onChange: sf('teamPrioritiesSheetGid'), placeholder: 'e.g. 538991154' },
      ],
      onSave: handleSaveTeamPriorities,
    },
    {
      key: 'notion-ai-tasks',
      title: 'Notion AI Tasks',
      isConfigured: !!team.notion_ai_tasks_configured,
      description: `Read-only mirror of the Artificial Intelligence Tasks database. Last synced: ${
        team.notion_ai_tasks_last_synced_at
          ? new Date(team.notion_ai_tasks_last_synced_at).toLocaleString()
          : 'never'
      }.`,
      fields: [
        {
          label: 'Database ID',
          type: 'text',
          value: f('notionAiTasksDatabaseId'),
          onChange: sf('notionAiTasksDatabaseId'),
          placeholder: 'Paste Notion database ID',
        },
        {
          label: 'API Token',
          type: 'password',
          value: f('notionAiTasksApiToken'),
          onChange: sf('notionAiTasksApiToken'),
          placeholder: team.notion_ai_tasks_api_token_preview || 'ntn_...',
        },
      ],
      onSave: handleSaveNotionAiTasks,
      onTest: handleTestNotionAiTasks,
      customContent: (
        <div className="integration-status-msg">
          <Button variant="outline" size="sm" onClick={handleSyncNotionAiTasks} isLoading={isLoading}>
            Sync now
          </Button>
        </div>
      ),
    },
    {
      key: 'stord',
      title: 'Stord (OOS Fulfillment)',
      isConfigured: !!team.stord_configured,
      fields: [
        { label: 'Base URL', type: 'text', value: f('stordBaseUrl'), onChange: sf('stordBaseUrl'), placeholder: 'https://api.stord.com' },
        { label: 'API Token', type: 'password', value: f('stordApiToken'), onChange: sf('stordApiToken'), placeholder: team.stord_api_token_preview || 'Enter API token' },
        { label: 'Org ID', type: 'text', value: f('stordOrgId'), onChange: sf('stordOrgId'), placeholder: 'Enter organization ID' },
        { label: 'Network ID', type: 'text', value: f('stordNetworkId'), onChange: sf('stordNetworkId'), placeholder: 'Enter network ID' },
        { label: 'Channel IDs (comma-separated)', type: 'text', value: f('stordChannelIds'), onChange: sf('stordChannelIds'), placeholder: 'channel-a, channel-b' },
        { label: 'Statuses (comma-separated)', type: 'text', value: f('stordStatuses'), onChange: sf('stordStatuses'), placeholder: 'backordered, exception' },
      ],
      onSave: handleSaveStord,
      onTest: team.stord_configured ? handleTestStord : undefined,
    },
    {
      key: 'shipbob',
      title: 'ShipBob (OOS Fulfillment)',
      isConfigured: !!team.shipbob_configured,
      fields: [
        { label: 'Base URL', type: 'text', value: f('shipbobBaseUrl'), onChange: sf('shipbobBaseUrl'), placeholder: 'https://api.shipbob.com/1.0' },
        { label: 'API Token', type: 'password', value: f('shipbobApiToken'), onChange: sf('shipbobApiToken'), placeholder: team.shipbob_api_token_preview || 'Enter API token' },
        { label: 'OOS exception reasons (comma-separated)', type: 'text', value: f('shipbobOosExceptionReasons'), onChange: sf('shipbobOosExceptionReasons'), placeholder: 'OutOfStock, NonPickable' },
      ],
      onSave: handleSaveShipbob,
      onTest: team.shipbob_configured ? handleTestShipbob : undefined,
    },
    {
      key: 'rippling',
      title: 'Rippling (Goals / KPIs)',
      isConfigured: !!team.rippling_configured,
      fields: [
        { label: 'API Key', type: 'password', value: f('ripplingApiKey'), onChange: sf('ripplingApiKey'), placeholder: team.rippling_api_key_preview || 'Enter API key' },
        { label: 'Company ID', type: 'text', value: f('ripplingCompanyId'), onChange: sf('ripplingCompanyId'), placeholder: team.rippling_company_id || 'Enter company ID' },
      ],
      onSave: handleSaveRippling,
    },
  ];

  const activeIntegration = integrations.find(i => i.key === selectedIntegration);

  return (
    <div id="teamSettingsPanel" className="settings-tab-panel">
      {/* Team Information */}
      <div className="settings-section">
        <h3 className="section-title">Team Information</h3>
        <div className="form-group">
          <label className="form-label">Team Name</label>
          <input
            type="text"
            id="teamNameSetting"
            className="form-input"
            value={team.name}
            readOnly
          />
        </div>
      </div>

      {/* Brand Assets */}
      <div className="settings-section">
        <h3 className="section-title">Brand Assets</h3>
        <div className="form-group">
          <label className="form-label">Website URL</label>
          <input
            type="url"
            id="websiteUrl"
            className="form-input"
            placeholder="https://www.your-store.com"
            value={websiteUrl}
            onChange={(e) => setWebsiteUrl(e.target.value)}
          />
          <div className="form-help">
            Your public storefront URL. Used for dogfooding, testimonials, and other features.
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Logo URL</label>
          <input
            type="url"
            id="logoUrl"
            className="form-input"
            placeholder="https://your-domain.com/logo.png"
            value={logoUrl}
            onChange={(e) => setLogoUrl(e.target.value)}
          />
          <div className="form-help">
            URL to your logo. Used on testimonial ads.
            Recommended: 200x200px or larger, PNG with transparent background.
          </div>
        </div>
        {logoUrl && (
          <div id="logoPreview" className="logo-preview-container">
            <img src={logoUrl} alt="Logo preview" className="settings-logo-preview-image" />
          </div>
        )}
        <Button onClick={handleSaveBrandAssets} isLoading={isLoading}>
          Save Brand Assets
        </Button>
      </div>

      {/* Assistant configuration */}
      <div className="settings-section">
        <h3 className="section-title">{BRANDING.assistantName} Assistant</h3>
        <div className="form-group">
          <label className="form-label">Name</label>
          <input
            type="text"
            className="form-input"
            placeholder={BRANDING.assistantName}
            value={f('aiName')}
            onChange={(e) => sf('aiName')(e.target.value)}
            maxLength={100}
          />
          <div className="form-help">
            Give your AI assistant a custom name. It will appear in the assistant panel and messages.
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Avatar URL</label>
          <input
            type="url"
            className="form-input"
            placeholder="Paste image URL"
            value={f('aiAvatarUrl')}
            onChange={(e) => sf('aiAvatarUrl')(e.target.value)}
            maxLength={500}
          />
          {f('aiAvatarUrl') && (
            <div className="ai-avatar-preview">
              <img src={f('aiAvatarUrl')} alt="AI avatar preview" className="ai-avatar-preview__image" />
            </div>
          )}
        </div>
        <div className="form-group">
          <label className="form-label">Persona</label>
          <textarea
            className="form-input"
            placeholder="Describe the AI's personality and role..."
            value={f('aiPersona')}
            onChange={(e) => sf('aiPersona')(e.target.value)}
            rows={3}
          />
          <div className="form-help">
            Define how the AI should behave. E.g. "Friendly and concise. Focus on actionable insights."
          </div>
        </div>
        <div className="form-group">
          <label className="form-label">Best Practices</label>
          <textarea
            className="form-input"
            placeholder="Team-specific instructions, product knowledge, tone guidelines..."
            value={f('aiBestPractices')}
            onChange={(e) => sf('aiBestPractices')(e.target.value)}
            rows={4}
          />
          <div className="form-help">
            Add team-specific instructions the AI should follow. This is like "post-training" for your assistant.
          </div>
        </div>
        <Button onClick={handleSaveAiPersona} isLoading={isLoading}>
          Save AI Persona
        </Button>
      </div>

      {/* Integrations Table */}
      <div className="settings-section">
        <h3 className="section-title">Integrations</h3>
        <div className="integrations-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>Integration</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {integrations.map((integration) => (
                <tr
                  key={integration.key}
                  onClick={() => setSelectedIntegration(integration.key)}
                >
                  <td>
                    <span className="integration-row-name">{integration.title}</span>
                  </td>
                  <td>
                    <StatusBadge
                      label={integration.isConfigured ? 'Connected' : 'Not Connected'}
                      tone={integration.isConfigured ? 'success' : 'neutral'}
                      size="sm"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Integration Detail Modal */}
      <Modal
        isOpen={!!activeIntegration}
        onClose={() => setSelectedIntegration(null)}
        title={activeIntegration?.title}
        size="md"
        footer={
          activeIntegration && (
            <div className="integration-actions">
              <Button size="sm" onClick={activeIntegration.onSave} isLoading={isLoading}>
                Save
              </Button>
              {activeIntegration.onTest && (
                <Button variant="outline" size="sm" onClick={activeIntegration.onTest}>
                  Test
                </Button>
              )}
            </div>
          )
        }
      >
        {activeIntegration && (
          <>
            {(activeIntegration.description || activeIntegration.docsUrl) && (
              <div className="integration-status-msg">
                {activeIntegration.description && <span>{activeIntegration.description}</span>}
                {activeIntegration.docsUrl && (
                  <>
                    {' '}
                    <a href={activeIntegration.docsUrl} target="_blank" rel="noreferrer">
                      View docs
                    </a>
                  </>
                )}
              </div>
            )}
            <div className="integration-fields">
              {activeIntegration.fields.map((field) => {
                const fieldSlug = field.label.replace(/\s+/g, '-').toLowerCase();
                const fieldDomName = `integration-${activeIntegration.key}-${fieldSlug}`;
                return (
                  <div key={`${activeIntegration.key}-${field.label}`} className="form-group">
                    <label className="form-label">{field.label}</label>
                    {field.type === 'textarea' ? (
                      <textarea
                        className="form-input integration-code-input"
                        placeholder={field.placeholder}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        rows={2}
                        autoComplete="off"
                        name={fieldDomName}
                      />
                    ) : (
                      <input
                        type={field.type}
                        className="form-input"
                        placeholder={field.placeholder}
                        value={field.value}
                        onChange={(e) => field.onChange(e.target.value)}
                        autoComplete={
                          field.type === 'password' ? 'new-password' : 'off'
                        }
                        name={fieldDomName}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {activeIntegration.customContent && activeIntegration.customContent}
          </>
        )}
      </Modal>

      {/* Team Members */}
      <div className="settings-section">
        <h3 className="section-title">Team Members</h3>
        <div id="membersList" className="members-list">
          {members.map((member: TeamMember) => (
            <div key={member.id} className="member-item">
              <div className="member-avatar">
                {member.picture ? (
                  <img src={member.picture} alt={member.name || member.email} />
                ) : (
                  <span>{(member.name || member.email).charAt(0).toUpperCase()}</span>
                )}
              </div>
              <div className="member-info">
                <span className="member-name">{member.name || member.email}</span>
                <span className="member-role">{member.role}</span>
              </div>
            </div>
          ))}
        </div>

        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setShowInvite(!showInvite)}
        >
          + Invite New Member
        </Button>

        {showInvite && (
          <div id="inviteMemberSection" className="invite-section">
            <h4 className="invite-section__title">Invite Team Member</h4>
            <div className="form-group">
              <label className="form-label">Email Address</label>
              <input
                type="email"
                className="form-input"
                placeholder="member@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Role</label>
              <select
                className="form-input"
                value={inviteRole}
                onChange={(e) => setInviteRole(e.target.value as 'member' | 'admin')}
              >
                <option value="member">Member</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <div className="button-row">
              <Button variant="outline" onClick={() => setShowInvite(false)}>
                Cancel
              </Button>
              <Button onClick={handleInviteMember} isLoading={isLoading}>
                Send Invitation
              </Button>
            </div>
          </div>
        )}
      </div>

      <AccessGroupsSection team={team} />
    </div>
  );
}

export default TeamSettings;
